import {Component, Vue} from 'vue-property-decorator'
import {GltfObject, HdriObject, IfObject, LightObject, Scene, SceneObject} from '@/scene'
import * as Three from 'three'
import {RGBELoader} from 'three/examples/jsm/loaders/RGBELoader'
import {fullStack} from 'make-error-cause'
import {GLTF, GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader'
import {KeyframeList} from '@/keyframe'
import {nglContext} from '@/components/NGL/nglContext'
import {storage} from '@/horizon-firebase'
import {SceneTemplate} from '@/store/templates'
import yaml from 'js-yaml'
import {DataTexture, EllipseCurve} from 'three'

// for accessing attributes by name
interface Indexable {
  [key: string]: any;
}

@Component({})
export default class SceneImportMixin extends Vue {
  showProgress = false
  progressPercent = 0

  get renderer() {
    return nglContext.get(this)?.renderer // XXX: use proper context key per NGL instance (how?)
  }
  get scene() {
    return nglContext.get(this)?.scene
  }

  protected onSceneImportLoad(_payload: any) {
    // TODO: Do we need to do anything when loading is finished?
  }

  protected onSceneImportProgress(progress: ProgressEvent) {
    this.progressPercent = Math.ceil(progress.loaded / (progress.total / 100))
    // console.log(`NGL.onSceneImportProgress(${Math.ceil(this.progressPercent)}%)`)
  }

  protected onSceneImportError(e: ErrorEvent) {
    // TODO: How to inform user about error? Toast message?
  }


  loadGLTF(url: string) {
    // Promise version of GLTFLoader.load
    const loader = new GLTFLoader()

    return new Promise<GLTF|undefined>((resolve, reject) => {
      try {
        loader.load(url,(gltf) => {
          if (this.onSceneImportLoad)
            this.onSceneImportLoad(gltf)
          return resolve(gltf)
        },(progressEvent) => {
          if (this.onSceneImportProgress)
            this.onSceneImportProgress(progressEvent)
          else
            console.log(`Loading ${url.substr(0, 80)}...${url.substr(-16)}: ${progressEvent.loaded} of ${progressEvent.total}`)
        },(errorEvent) => {
          if (this.onSceneImportError)
            this.onSceneImportError(errorEvent)
          return reject(errorEvent.error)
        })
      } catch (err) {
        console.error(`loadGLTF: Error loading glTF from ${url}: ${fullStack(err)}`)
        return resolve(undefined)
      }
    }).catch((err) => Promise.reject(err))
  }

  async createTransform(obj: SceneObject, parent: Three.Object3D) {
    const newObj = new Three.Group()
    newObj.name = obj.name
    if (obj.id)
      newObj.userData.id = obj.id
    parent.add(newObj)
    return newObj
  }

  async createObject(obj: GltfObject, parent: Three.Object3D) {
    try {
      const gltf = await this.loadGLTF(obj.url as string)
      if (!gltf)
        return
      // gltf has members: .scene, .animations, .scenes, .cameras, .asset
      // We just use the first scene here
      // XXX: what about glTF with multiple scenes, or cameras etc.?
      if (!gltf.scene.name || gltf.scene.name === 'Scene')
        gltf.scene.name = obj.name
      gltf.scene.userData.name = obj.name
      gltf.scene.userData.importedFrom = obj.url
      if (obj.scale)
        gltf.scene.scale.set(obj.scale[0], obj.scale[1], obj.scale[2])
      if (obj.position)
        gltf.scene.position.set(obj.position[0], obj.position[1], obj.position[2])
      if (obj.rotation) {
        const r = obj.rotation
        const quaternion = new Three.Quaternion(r[0], r[1], r[2], r[3])
        gltf.scene.setRotationFromQuaternion(quaternion)
      }
      parent.add(gltf.scene)
      return gltf.scene
    } catch (err) {
      console.log(`Can't load glTF object ${obj.name} from ${obj.url}: ${err}`)
      return
    }
  }

  setObjAttributeKeyframeable(obj: Three.Object3D & Indexable, attribute: string, data: number[] | KeyframeList) {
    if (data instanceof KeyframeList)
      console.log('scene import: no keyframe support for position yet')
    else
      obj[attribute].set(data[0], data[1], data[2])
  }

  async createLight(obj: LightObject, parent: Three.Object3D) {
    // light types: directional, spot, ambient, hemisphere, area
    // three.js doesn't support area, so we substitute a point
    let light: Three.Light | undefined

    const makeColor = (rgb: { r: number; g: number; b: number } | undefined) => {
      if (!rgb)
        return new Three.Color(1, 1, 1)
      if (rgb.r === undefined || rgb.g === undefined || rgb.b === undefined) {
        console.error(`Can't load color for light; r/g/b undefined`)
        return new Three.Color(1, 1, 1)
      }
      return new Three.Color(rgb.r, rgb.g, rgb.b)
    }

    switch (obj.subtype) {
    case 'directional': {
      const color = makeColor(obj.color)
      light = new Three.DirectionalLight(color, obj.intensity)
      if (obj.position)
        this.setObjAttributeKeyframeable(light, 'position', obj.position)
      if (obj.target) {
        // need a target object to point at
        const targetObj = new Three.Object3D()
        targetObj.name = obj.name + '-target'
        this.setObjAttributeKeyframeable(targetObj, 'position', obj.target)
        parent.add(targetObj);
        (light as Three.DirectionalLight).target = targetObj
      } else if (obj.direction) {
        // ignore: three.js doesn't support directional light direction -- only target
      }
      break
    }
    case 'spot': {
      const color = makeColor(obj.color)
      light = new Three.SpotLight(color, obj.intensity, obj.distance, obj.angle, obj.penumbra, obj.decay)
      if (obj.position)
        this.setObjAttributeKeyframeable(light, 'position', obj.position)
      if (obj.target) {
        // need a target object to point at
        const targetObj = new Three.Object3D()
        targetObj.name = obj.name + '-target'
        this.setObjAttributeKeyframeable(targetObj, 'position', obj.target)
        parent.add(targetObj)
        ;(light as Three.DirectionalLight).target = targetObj
      } else if (obj.direction) {
        // ignore: three.js doesn't support directional light direction -- only target
      }
      break
    }
    case 'area': // fall through
    case 'point': {
      const color = makeColor(obj.color)
      light = new Three.PointLight(color, obj.intensity, obj.distance, obj.decay)
      if (obj.position)
        this.setObjAttributeKeyframeable(light, 'position', obj.position)
      break
    }
    case 'hemisphere': {
      const skyColor = makeColor(obj.skyColor)
      const groundColor = makeColor(obj.groundColor)
      light = new Three.HemisphereLight(skyColor, groundColor, obj.intensity)
      break
    }
    case 'ambient': {
      const color = makeColor(obj.color)
      light = new Three.AmbientLight(color, obj.intensity)
      break
    }
    default: {
      console.log(`Ignoring light subtype ${obj.subtype}; not implemented yet.`)
      break
    }
    }
    if (light) {
      light.name = obj.name
      parent.add(light)
    }
    if (light && obj.visible !== undefined)
      light.visible = obj.visible

    return light
  }

  async loadHdri(url: string): Promise<Three.DataTexture> {
    return new Promise((resolve, reject) => {
      const loader = new RGBELoader()
        .setDataType(Three.UnsignedByteType)
      loader.load(url,(texture: DataTexture) => {
        if (this.onSceneImportLoad)
          this.onSceneImportLoad(texture)
        resolve(texture)
      },xhr => {
        if (this.onSceneImportProgress)
          this.onSceneImportProgress(xhr)
        else
          console.log(`Loading ${url}: ${xhr.loaded} of ${xhr.total}`)
      },err => {
        if (this.onSceneImportError)
          this.onSceneImportError(err)
        console.error(`Error loading ${url}: ${err}`)
        reject(err)
      })
    })
  }

  async createHdri(obj: HdriObject, parent: Three.Object3D) {
    try {
      const useEnvironmentMap = true // use w/ three r112 and later, see examples
      if (useEnvironmentMap) {
        return new Promise<void>((resolve, reject) => {
          if (!this.renderer) { // shouldn't happen
            resolve()
            return
          }
          const pmremGenerator = new Three.PMREMGenerator(this.renderer)
          pmremGenerator.compileEquirectangularShader()
          new RGBELoader()
            .setDataType(Three.UnsignedByteType)
            .load(obj.url, (texture) => {
              // texture is loaded
              // console.log(`Loaded HDRi texture ${obj.url}`)
              const envMap = pmremGenerator.fromEquirectangular(texture).texture
              if (this.scene) {   // just to make Typescript happy
                this.scene.background = envMap
                this.scene.environment = envMap
              }
              texture.dispose()
              pmremGenerator.dispose()
              resolve()
            }, progressEvent => {
              if (progressEvent.lengthComputable)
                console.log(`Loading texture... ${progressEvent.loaded}/${progressEvent.total}`) // progress
              else
                console.log(`Loading texture ${obj.url}...`)
            }, error => {
              console.error(`Error Loading texture ${obj.url}: ${error.message}`)
              reject(error)
            })
        })
      } else {
        // use big sphere - this doesn't light the objects but at least it shows up
        const texture = await this.loadHdri(obj.url)
        const envSphere = new Three.Mesh(
          new Three.SphereGeometry(10000, 32, 32),
          new Three.MeshBasicMaterial({side: Three.DoubleSide, map: texture})
        )
        envSphere.userData = {DSS: {objType: 'hdri', url: obj.url, name: obj.name}} // mark for export
        envSphere.name = 'Environment Sphere'
        parent.add(envSphere)
      }
    } catch (err) {
      console.log(`Error loading HDR texture ${obj.url}: ${fullStack(err)}`)
    }
  }

  async importSceneObj(obj: SceneObject, parent: Three.Object3D, storagePath: string) {
    if (this.scene === null || this.renderer === null)
      return

    if (obj.if && obj.if !== 'frontEnd')
      return                    // ignore backEnd subtree
    // get GCS url so we can download it
    // Ideally could do this in parallel for all urls up front
    if (obj.type === 'object' || obj.type === 'hdri')
      obj.url = await storage().ref(`${storagePath}/${(obj as any).url}`).getDownloadURL()

    // dispatch based on obj.type: transform, object (glTF), light, camera
    let threeObj: Three.Object3D | undefined

    // console.log(`importSceneObj(${obj.type} ${obj.name})`)
    if (obj.type === 'if' && (obj as IfObject).cond !== 'glTF') {
      // This subtree is not for us; ignore
      return
    }
    try {
      switch (obj.type) {
      case 'group':
      case 'transform': {
        threeObj = await this.createTransform(obj, parent)
        break
      }
      case 'object': {
        threeObj = await this.createObject(obj as GltfObject, parent)
        break
      }
      case 'light': {
        threeObj = await this.createLight(obj as LightObject, parent)
        break
      }
      case 'camera': {
        break
      }
      case 'hdri': {
        await this.createHdri(obj as HdriObject, parent) // note: Hdri applies to scene as a whole
        // This node shouldn't have any children since there's no object created
        return
      }
      default: {
        console.log(`Ignoring object ${obj.name} of type ${obj.type}: not implemented yet.`)
        break
      }
      }
      // now process children (could do this in parallel...)
      if (obj.children && threeObj !== undefined) {
        for (const child of obj.children)
          await this.importSceneObj(child, threeObj, storagePath)
      }
    } catch (err) {
      console.error(`Error in importSceneObj(${obj.type} ${obj.name}): ${fullStack(err)}`)
    }
  }

  async importBgScene(bgScene: Scene, name: string, parent: Three.Object3D,
                      storagePathDir: string, userData: any) {
    const threeScene = this.scene

    if (threeScene === null || this.renderer === null)
      return

    const obj = new Three.Group()
    // obj.name = `${name}: imported ${scene.project}`
    obj.name = name // this name is required while changing scene backgrounds!
    // Tag our userData so we can find it at export time
    // I think we'll use the name to find the corresponding blender scene
    obj.userData = userData
    // in theory this could load multiple objects in parallel, but that seems dangerous, so
    // we'll await each one for now
    for (const sceneObj of bgScene.scene)
      await this.importSceneObj(sceneObj, obj, storagePathDir)

    parent.add(obj)
    console.log(`importScene(${bgScene.project}, ${name}): done.`)
  }

  async importSceneFromUrl(url: string, name: string, parent: Three.Object3D,
                           storagePathDir: string, userData: any) {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'same-origin',
      redirect: 'follow',
    })
    if (response.ok) {
      try {
        const data = await response.text()
        if (data) {
          const scene: Scene = yaml.load(data)
          userData = {...userData} // clone, don't bash original
          userData.importedFPS = scene.fps
          userData.importedDurationSec = scene.duration_sec
          return await this.importBgScene(scene, name, parent, storagePathDir, userData)
        } else {
          console.error(`Can't parse scene data from ${url}`)
          return undefined
        }
      } catch (err) {
        console.error(`Can't load bg scene from ${url}`, err)
      }
    } else {
      console.error(`Couldn't retrieve scene from ${url}: ${response.status}: ${response.statusText}`)
    }

  }

  async importSceneFile(id: string, name: string, storagePath: string, storagePathDir: string,
                        parent: Three.Object3D) {
    const threeScene = this.scene

    if (threeScene === null || this.renderer === null)
      return

    if (storagePath) {
      const sceneFileUrl = await storage().ref(storagePath).getDownloadURL()
      const userData = {
        DSS: {
          objType: 'bgScene',
          templateDocId: id,
          url: storagePath,
          name: name,
        }
      }
      await this.importSceneFromUrl(sceneFileUrl, name, parent, storagePathDir, userData)
    }
  }
}
